import React from "react";
import { ImageGeneratorCatalogColourStory } from "../../app/api/imageGenerator";
import ImageGeneratorMakeOwnPicker from "./ImageGeneratorMakeOwnPicker";
import ImageGeneratorSelectionTick from "./ImageGeneratorSelectionTick";
import {
  PaletteChip,
  RequiredMark,
  chipClassName,
  helperTextClassName,
  sectionClassName,
  sectionTitleClassName,
} from "./ImageGeneratorControls";
import { PALETTE_EXTRACT_ACCEPT } from "./shared/generativeCatalog";
import {
  DENIM_PALETTE_MAKE_OWN_HINT,
  DENIM_PALETTE_UPLOAD_HELPER,
  PALETTE_UPLOAD_HELPER,
} from "./collectionBuildConstants";
import { ReactComponent as DropdownArrow } from "../../assets/icons/dropdownArrow.svg";

type CustomPalette = {
  hexes: string[];
  title: string;
  swatches?: Array<{
    hex: string;
    name: string;
    tier: "major" | "minor";
  }>;
};

type Props = {
  colourStories: ImageGeneratorCatalogColourStory[];
  customPalettes: CustomPalette[];
  selectedPaletteIndex: number;
  onSelectStory: (index: number, id: string) => void;
  onSelectCustom: (index: number) => void;
  onEditCustom?: (customIndex: number) => void;
  onClear: () => void;
  makeOwnOpen: boolean;
  makeOwnRef: React.Ref<HTMLDivElement>;
  onToggleMakeOwn: () => void;
  makeOwnSlots: Array<string | null>;
  makeOwnSlotIndex: number;
  onSelectMakeOwnSlot: (index: number) => void;
  onChangeMakeOwnSlot: (index: number, hex: string) => void;
  onAddMakeOwnColour: (hex: string) => void;
  onAddMakeOwnSlot: () => void;
  onRemoveMakeOwnSlot: (index: number) => void;
  onUploadClick: () => void;
  paletteExtracting: boolean;
  uploadInputRef: React.Ref<HTMLInputElement>;
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  variant?: "default" | "denim";
};

/** Squared rounded colour tile + top-left tick + label (denim cast picker). */
const DenimColourSwatch: React.FC<{
  title: string;
  hex: string;
  isActive: boolean;
  isMuted: boolean;
  onClick: () => void;
}> = ({ title, hex, isActive, isMuted, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={title}
    aria-pressed={isActive}
    title={title}
    className={`inline-flex w-[52px] shrink-0 flex-col items-center gap-1 outline-none focus-visible:outline-none sm:w-[56px] ${
      isMuted ? "opacity-45 hover:opacity-100" : ""
    }`}
  >
    <span
      className={`relative block aspect-square w-full overflow-hidden rounded-xl transition-all ${
        isActive
          ? "border-2 border-[#737373]"
          : "border border-[#737373]/50 hover:border-[#737373]"
      }`}
      style={{ backgroundColor: hex || "#CCCCCC" }}
    >
      {isActive ? (
        <ImageGeneratorSelectionTick
          className="left-1 top-1 h-3.5 w-3.5"
          iconClassName="h-2 w-2.5"
        />
      ) : null}
    </span>
    <span className="line-clamp-2 w-full text-center text-[10px] font-normal leading-snug text-secondary">
      {title}
    </span>
  </button>
);

const ImageGeneratorPaletteSection: React.FC<Props> = ({
  colourStories,
  customPalettes,
  selectedPaletteIndex,
  onSelectStory,
  onSelectCustom,
  onEditCustom,
  onClear,
  makeOwnOpen,
  makeOwnRef,
  onToggleMakeOwn,
  makeOwnSlots,
  makeOwnSlotIndex,
  onSelectMakeOwnSlot,
  onChangeMakeOwnSlot,
  onAddMakeOwnColour,
  onAddMakeOwnSlot,
  onRemoveMakeOwnSlot,
  onUploadClick,
  paletteExtracting,
  uploadInputRef,
  onUpload,
  variant = "default",
}) => {
  const hasPaletteSelected = selectedPaletteIndex >= 0;
  const isDenim = variant === "denim";

  const makeOwnControl = (
    <div className="relative shrink-0 self-start" ref={makeOwnRef}>
      {!makeOwnOpen ? (
        <button
          type="button"
          onClick={onToggleMakeOwn}
          className={chipClassName(false)}
          title="Make your own palette"
        >
          + Make your own
          <span>
            <DropdownArrow className="h-2 w-2" />
          </span>
        </button>
      ) : (
        <ImageGeneratorMakeOwnPicker
          slots={makeOwnSlots}
          selectedSlot={makeOwnSlotIndex}
          onSelectSlot={onSelectMakeOwnSlot}
          onChangeSlot={onChangeMakeOwnSlot}
          onAddCurrent={onAddMakeOwnColour}
          onAddSlot={onAddMakeOwnSlot}
          onRemoveSlot={onRemoveMakeOwnSlot}
          onToggle={onToggleMakeOwn}
        />
      )}
    </div>
  );

  const uploadWithHelper = (
    helper: string,
    italic = false,
    maxWidthClass = "max-w-[13.5rem]"
  ) => (
    <div
      className={`flex shrink-0 flex-col items-start self-start ${maxWidthClass}`}
    >
      <button
        type="button"
        onClick={onUploadClick}
        disabled={paletteExtracting}
        className={`${chipClassName(false)} disabled:cursor-wait disabled:opacity-60`}
        title="Upload an image to extract a fashion palette"
      >
        {paletteExtracting ? "Extracting..." : "Upload"}
      </button>
      <input
        ref={uploadInputRef}
        type="file"
        accept={PALETTE_EXTRACT_ACCEPT}
        className="hidden"
        onChange={onUpload}
      />
      <p className={`${helperTextClassName} mt-1.5${italic ? " italic" : ""}`}>
        {helper}
      </p>
    </div>
  );

  if (isDenim) {
    return (
      <div className={sectionClassName}>
        <h2 className={`${sectionTitleClassName} mb-3`}>
          Select Colour Palette
          <RequiredMark />
        </h2>
        <div className="flex flex-nowrap items-start gap-2 overflow-x-auto pb-1 sm:gap-2.5">
          {colourStories.map((palette, idx) => {
            const isActive = selectedPaletteIndex === idx;
            const hex =
              palette.palette.find(swatch => swatch.hex)?.hex ||
              palette.palette[0]?.hex ||
              "#CCCCCC";
            return (
              <DenimColourSwatch
                key={palette.id}
                title={palette.title}
                hex={hex}
                isActive={isActive}
                isMuted={hasPaletteSelected && !isActive}
                onClick={() => {
                  if (isActive) {
                    onClear();
                    return;
                  }
                  onSelectStory(idx, palette.id);
                }}
              />
            );
          })}

          {customPalettes.map((palette, idx) => {
            const paletteIndex = colourStories.length + idx;
            const isActive = selectedPaletteIndex === paletteIndex;
            return (
              <DenimColourSwatch
                key={`custom-${idx}`}
                title={palette.title}
                hex={palette.hexes[0] || "#CCCCCC"}
                isActive={isActive}
                isMuted={hasPaletteSelected && !isActive}
                onClick={() => {
                  if (isActive) {
                    onClear();
                    return;
                  }
                  onSelectCustom(paletteIndex);
                  onEditCustom?.(idx);
                }}
              />
            );
          })}

          <div className="ml-1 flex shrink-0 flex-nowrap items-start gap-3 pt-0.5">
            {makeOwnControl}
            {uploadWithHelper(
              DENIM_PALETTE_UPLOAD_HELPER,
              false,
              "max-w-[14rem]"
            )}
          </div>
        </div>
        <p className={`${helperTextClassName} mt-3 max-w-xl`}>
          {DENIM_PALETTE_MAKE_OWN_HINT}
        </p>
      </div>
    );
  }

  return (
    <div className={sectionClassName}>
      <h2 className={`${sectionTitleClassName} mb-3`}>
        Select Colour Palette
        <RequiredMark />
      </h2>
      <div className="flex flex-wrap items-start gap-x-2 gap-y-2">
        {colourStories.map((palette, idx) => {
          const isActive = selectedPaletteIndex === idx;
          return (
            <PaletteChip
              key={palette.id}
              title={palette.title}
              colors={palette.palette.map(swatch => swatch.hex)}
              swatches={palette.palette}
              idPrefix={palette.id}
              isActive={isActive}
              isMuted={hasPaletteSelected && !isActive}
              onClick={() => {
                if (isActive) {
                  onClear();
                  return;
                }
                onSelectStory(idx, palette.id);
              }}
            />
          );
        })}

        {customPalettes.map((palette, idx) => {
          const paletteIndex = colourStories.length + idx;
          const isActive = selectedPaletteIndex === paletteIndex;
          return (
            <PaletteChip
              key={`custom-${idx}`}
              title={palette.title}
              colors={palette.hexes}
              swatches={palette.swatches}
              idPrefix={`custom-${idx}`}
              isActive={isActive}
              isMuted={hasPaletteSelected && !isActive}
              onClick={() => {
                if (isActive) {
                  onClear();
                  return;
                }
                onSelectCustom(paletteIndex);
                onEditCustom?.(idx);
              }}
            />
          );
        })}

        {makeOwnControl}
        {uploadWithHelper(PALETTE_UPLOAD_HELPER, true)}
      </div>
    </div>
  );
};

export default ImageGeneratorPaletteSection;
